import {
  DimensionSide,
  Element,
  ElementType,
  Fixture,
  FixtureType,
  Polyline,
  Rect,
  Symbol as SymbolElement,
  SymbolType,
  Text,
} from '@ritabygglov/core';
import { createAngle } from './Angle';
import { createFixture } from './Fixture';
import { createPolyline } from './Polyline';
import { createRect } from './Rect';
import { createSymbol } from './Symbol';
import { createText } from './Text';
import { createTrapezoid } from './Trapezoid';
import { createTriangle } from './Triangle';

export interface CreateElementInput {
  initialX: number;
  initialY: number;
  renderingOrder: number;
  id: string;
  userId: string;
  symbolType?: SymbolType;
  fixtureType?: FixtureType;
  options?: Partial<Element>;
}

export interface ElementFactory {
  create: (input: CreateElementInput) => Element;
  // A polyline is provisional while the user is still placing its points, so
  // its create change must not land in the undo history.
  createIsEphemeral?: boolean;
}

// Total by construction: adding a member to ElementType is a compile error
// until it is given a factory here, or explicitly marked as not directly
// creatable with `null`.
export const ELEMENT_FACTORIES: Record<ElementType, ElementFactory | null> = {
  [ElementType.Rect]: {
    create: ({ initialX, initialY, renderingOrder, id, userId, options }) =>
      createRect(
        initialX,
        initialY,
        renderingOrder,
        id,
        userId,
        options as Partial<Rect>
      ),
  },
  [ElementType.Text]: {
    create: ({ initialX, initialY, renderingOrder, id, userId, options }) =>
      createText(
        initialX,
        initialY,
        renderingOrder,
        id,
        userId,
        options as Partial<Text>
      ),
  },
  [ElementType.Polyline]: {
    create: ({ initialX, initialY, renderingOrder, id, userId, options }) =>
      createPolyline(
        {
          x: initialX,
          y: initialY,
          dimensionSide: DimensionSide.Above,
        },
        renderingOrder,
        id,
        userId,
        undefined,
        undefined,
        options as Partial<Polyline>
      ),
    createIsEphemeral: true,
  },
  [ElementType.Symbol]: {
    create: ({
      initialX,
      initialY,
      renderingOrder,
      id,
      userId,
      symbolType,
      options,
    }) =>
      createSymbol(
        initialX,
        initialY,
        renderingOrder,
        id,
        userId,
        symbolType,
        options as Partial<SymbolElement>
      ),
  },
  [ElementType.Fixture]: {
    create: ({
      initialX,
      initialY,
      renderingOrder,
      id,
      userId,
      fixtureType,
      options,
    }) =>
      createFixture(
        initialX,
        initialY,
        renderingOrder,
        id,
        userId,
        fixtureType,
        options as Partial<Fixture>
      ),
  },
  [ElementType.Triangle]: {
    create: ({ initialX, initialY, renderingOrder, id, userId }) =>
      createTriangle(initialX, initialY, renderingOrder, id, userId),
  },
  [ElementType.Trapezoid]: {
    create: ({ initialX, initialY, renderingOrder, id, userId }) =>
      createTrapezoid(initialX, initialY, renderingOrder, id, userId),
  },
  [ElementType.Angle]: {
    create: ({ initialX, initialY, renderingOrder, id, userId }) =>
      createAngle(initialX, initialY, renderingOrder, id, userId),
  },
  // A group is formed from elements that already exist, so it has no factory
  // on this path — see helpers/groupHelpers.
  [ElementType.Group]: null,
};
